/**
 * AnswerGroundingMeter — a small grey signal-strength glyph under a twin's avatar.
 *
 * Shows how much of the twin's final answer rests on its own profile records,
 * from the level Nest stores at `msg.metadata.answerGrounding`. The level set is
 * the shared-types contract; anything outside it (or absent) renders nothing.
 * Three bars of rising height (short, medium, tall); the filled count encodes the
 * level (estimate 1, related 2, recorded 3; declined 1, a refusal, told apart only by
 * its sentence) and unfilled bars stay a lighter grey.
 * The glyph stays grey at every level; the instant PlotTooltip carries one sentence.
 */

import { useTranslation } from 'react-i18next'

import { PlotTooltip } from '@digital-twins/deck-ui/components/charts/PlotTooltip'
import { cn } from '@/lib/utils'

import { ANSWER_GROUNDING_LEVELS, AnswerGroundingLevel } from '@digital-twins/shared-types'

/** Bar heights, shortest first; their count is the glyph's bar count. */
const BAR_HEIGHT_CLASSES: Array<string> = ['h-[3px]', 'h-[5px]', 'h-[8px]']

const FILLED_BARS: Record<AnswerGroundingLevel, number> = {
  declined: 1,
  estimate: 1,
  related: 2,
  recorded: 3,
}

function isAnswerGroundingLevel(value: unknown): value is AnswerGroundingLevel {
  return (
    typeof value === 'string' && (ANSWER_GROUNDING_LEVELS as ReadonlyArray<string>).includes(value)
  )
}

/** Reads the grounding level off a message's free metadata; unknown or absent yields null. */
export function readAnswerGrounding(metadata: unknown): AnswerGroundingLevel | null {
  if (!metadata || typeof metadata !== 'object') {
    return null
  }
  const level = (metadata as Record<string, unknown>).answerGrounding
  return isAnswerGroundingLevel(level) ? level : null
}

export interface AnswerGroundingMeterProps {
  messageId: string
  level: unknown
}

export function AnswerGroundingMeter(props: AnswerGroundingMeterProps) {
  const { messageId, level } = props

  const { t } = useTranslation('app')

  if (!isAnswerGroundingLevel(level)) {
    return null
  }

  const filledBars = FILLED_BARS[level]
  const sentence = t(`chatChrome.answerGrounding.${level}`)

  return (
    <PlotTooltip
      value={sentence}
      triggerAriaLabel={sentence}
      align="start"
      side="bottom"
      tooltipClassName="border border-border"
    >
      <span
        className="inline-flex h-[8px] items-end gap-[2px]"
        data-testid={`chat-answer-grounding-${messageId}`}
        data-level={level}
      >
        {BAR_HEIGHT_CLASSES.map((heightClass, i) => (
          <span
            key={i}
            data-testid={`chat-answer-grounding-${messageId}-tick-${i}`}
            data-filled={i < filledBars ? 'true' : 'false'}
            className={cn(
              'block w-[2px] rounded-[1px]',
              heightClass,
              i < filledBars ? 'bg-twin-chat-fg-muted/60' : 'bg-twin-chat-fg-muted/20',
            )}
          />
        ))}
      </span>
    </PlotTooltip>
  )
}
